import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EmbeddedViewRef,
  Injectable,
} from '@angular/core';
import type { LngLatLike, MapboxGeoJSONFeature, Popup } from 'mapbox-gl';
import { CellGeoData } from './geocoder-cache';
import { LEVEL_LAYER, ORI_LAYER } from './layer-store.service';
import { MapBoxService } from '../mapbox.service';
import { schoolsPoiLayers } from '../../menu/right-menu/layers-menu/education/education.service';
import { MapHttpService } from './map-http.service';
import { DatePipe } from '@angular/common';
import { BreakpointObserverService } from '../../../shared/services/breakpoint-observer.service';
import { Feature, Position } from 'geojson';
import {
  DEFAULT_POPUP,
  HOVER_POPUP,
  MapPopupComponent,
  ORI_POPUP,
  PopupData,
  SCHOOLS_POPUP,
} from '../visualization/map-popup/map-popup.component';
import { SearchGeocoderService } from '../../../shared/services/search-geocoder.service';
import { SelectedCellService } from './selected-cell.service';
import { pointsLayersList } from '../../menu/right-menu/layers-menu/points.service';
import { ReportsService } from '../../../reports/reports.service';
import { geocoderContextToString } from '../../../shared/util/geocoder-context-to-string';
import { convertLayerIdToCellType } from '../../../shared/util/layerIdToCellType';
import { Router } from '@angular/router';
import { ReportTypes } from '../../../user/user/user.model';

declare const mapboxgl: typeof import('mapbox-gl');

export const CLICK = 'CLICK';
export const HOVER = 'HOVER';

export type PopupActionType = typeof CLICK | typeof HOVER;

interface PopupOffsetConfig {
  offsetXFactor?: number;
  offsetYTopFactor?: number;
  offsetYMidFactor?: number;
  offsetYBottomFactor?: number;
}

@Injectable({
  providedIn: 'root',
})
export class PopupService {
  public popupTitle!: string;
  public popup!: Popup;

  public isSeeMoreActive: boolean = true;

  public currentlySelectedId: string | null = null;
  public currentlySelectedLevel: LEVEL_LAYER | null = null;
  public popupComponentRef?: ComponentRef<MapPopupComponent>;

  private topPosition!: number;
  private leftPosition!: number;

  private userMovedPopup = false;

  constructor(
    private mapboxService: MapBoxService,
    private mapHttpService: MapHttpService,
    private datePipe: DatePipe,
    private isMobileService: BreakpointObserverService,
    private geocoderService: SearchGeocoderService,
    private selectedCellService: SelectedCellService,
    private appRef: ApplicationRef,
    private reportsService: ReportsService,
    private router: Router
  ) {}

  public handlePopup(
    feature: MapboxGeoJSONFeature,
    coordinates: LngLatLike,
    actionType: PopupActionType
  ): void {
    if (this.popup && this.popup.isOpen()) {
      this.popup.remove();
    }

    this.userMovedPopup = false;

    this.currentlySelectedId = feature.properties!.external_id;
    this.currentlySelectedLevel = feature.layer.id as LEVEL_LAYER;

    const data: PopupData = {
      type: this.getPopupType(feature, actionType),
      properties: { ...feature.properties, ...feature.state },
      feature: feature,
    };

    if (pointsLayersList.includes(feature.layer.id)) {
      this.handlePointsPopup(feature);
    }

    this.initPopup(coordinates);
    this.setPopupContent(data);
  }

  public getPopupType(
    feature: MapboxGeoJSONFeature,
    actionType: PopupActionType
  ): PopupData['type'] {
    return actionType === HOVER
      ? HOVER_POPUP
      : schoolsPoiLayers.includes(feature.layer.id)
        ? SCHOOLS_POPUP
        : feature.layer.id === ORI_LAYER
          ? ORI_POPUP
          : DEFAULT_POPUP;
  }

  public initPopup(coordinates: mapboxgl.LngLatLike): void {
    const isMobile = this.isMobileService.isMobile.getValue();
    const { anchor, offset } = {
      anchor: 'top' as mapboxgl.Anchor,
      offset: [0, 0] as mapboxgl.Offset,
    };

    // Assign popup instance on first use with params related to mobile/desktop
    // as they are not changebale after init and we want to keep single popup instance
    // to manipulate it from geocoderService
    if (isMobile) {
      if (!this.popup) {
        this.popup = new mapboxgl.Popup({
          anchor: anchor,
          offset: offset,
          maxWidth: '345px',
          className: 'default-popup',
          closeButton: false,
          closeOnClick: false,
        }).setLngLat(coordinates);
      }

      this.centerPopupOnMobile(coordinates);
    } else {
      if (!this.popup) {
        this.popup = new mapboxgl.Popup({
          maxWidth: '400px',
          className: 'default-popup',
          closeButton: false,
          closeOnClick: false,
        });
      }

      this.setDesktopPopupPosition(
        coordinates,
        this.mapboxService.map,
        this.popup
      );
    }
  }

  public setDesktopPopupPosition(
    coordinates: mapboxgl.LngLatLike,
    map: mapboxgl.Map,
    popup: mapboxgl.Popup,
    config?: PopupOffsetConfig
  ): void {
    if (this.userMovedPopup) {
      // if user moved it manually, do not auto reposition
      return;
    }

    // Defaults:
    const offsetXFactor = config?.offsetXFactor ?? 0.1;
    const offsetYTopFactor = config?.offsetYTopFactor ?? 0.25;
    const offsetYMidFactor = config?.offsetYMidFactor ?? 0.1;
    const offsetYBottomFactor = config?.offsetYBottomFactor ?? 0.1;

    // Project the coordinates to pixels
    const projected = map.project(coordinates);

    // Get map container sizes
    const mapContainer = map.getContainer();
    const mapWidth = mapContainer.offsetWidth;
    const mapHeight = mapContainer.offsetHeight;

    // Calculate percentages for x and y relative to map width and height
    const xPercentage = (projected.x / mapWidth) * 100;
    const yPercentage = (projected.y / mapHeight) * 100;

    const newPoint = { x: projected.x, y: projected.y };
    let offsetX = 0;
    let offsetY = 0;

    // Horizontal offset: adjust differently depending on which half of the map the point is in
    if (xPercentage > 50) {
      offsetX = -mapWidth * offsetXFactor;
    } else {
      offsetX = mapWidth * offsetXFactor;
    }

    // Vertical offset: use different factors based on the y position
    if (yPercentage < 30) {
      offsetY = mapHeight * offsetYTopFactor;
    } else if (yPercentage >= 30 && yPercentage < 80) {
      offsetY = -mapHeight * offsetYMidFactor;
    } else if (yPercentage >= 80) {
      offsetY = -mapHeight * offsetYBottomFactor;
    }

    newPoint.x += offsetX;
    newPoint.y += offsetY;

    // Unproject the new point and update the popup location
    const newCoord = map.unproject(newPoint as mapboxgl.PointLike);
    popup.setLngLat(newCoord);
  }

  public setPopupTopRight(
    map: mapboxgl.Map,
    popup: mapboxgl.Popup,
    margin: number = 10
  ): void {
    // Get the map container dimensions
    const mapContainer = map.getContainer();
    const mapWidth = mapContainer.offsetWidth;
    // Use the margin for both x and y directions
    const topRightPixel = { x: mapWidth - margin, y: 5 };
    // Convert pixel position to geographical coordinates
    const topRightCoord = map.unproject(topRightPixel as mapboxgl.PointLike);
    // Set the popup's location to the computed coordinates
    popup.setLngLat(topRightCoord);
  }

  private centerPopupOnMobile(coordinates: LngLatLike): void {
    const map = this.mapboxService.map;
    if (map) {
      map.flyTo({
        center: coordinates,
        zoom: map.getZoom(),
      });
    }
  }

  public handlePopupTitle(feature?: MapboxGeoJSONFeature): void {
    this.setTitle('');

    const selectedFeature =
      feature ?? this.selectedCellService.getSelectedFeature();

    const id = selectedFeature?.id;
    const center = this.mapboxService.getFeatureCenter(selectedFeature!);

    if (!this.geocoderService.geocoderCache.getCellData(id)) {
      this.handleGeocoderRequest(id, center).then((res) => {
        this.geocoderService.geocoderCache.addCell(res.id, res.data);
        this.updateTitle(selectedFeature!);
      });
    } else {
      this.updateTitle(selectedFeature!);
    }
  }

  public reapplyCssPosition(): void {
    const el = this.popup?.getElement() as HTMLElement | null;
    if (!el) return;
    requestAnimationFrame(() => {
      el.style.setProperty('left', `${this.leftPosition}px`);
      el.style.setProperty('top', `${this.topPosition}px`);
    });
  }

  private setPopupContent(data: PopupData): void {
    this.popupComponentRef = createComponent(MapPopupComponent, {
      environmentInjector: this.appRef.injector,
    });

    this.popupComponentRef.instance.data = data;

    this.appRef.attachView(this.popupComponentRef.hostView);

    const domElem = (this.popupComponentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;

    this.popup.setDOMContent(domElem).addTo(this.mapboxService.map);

    let popupEl = this.popup.getElement() as HTMLElement;
    const map = this.mapboxService.map;

    const ro = new ResizeObserver((entries) => {
      popupEl = this.popup.getElement() as HTMLElement;
      if (!popupEl) return;

      // We will compute rawX, rawY differently depending on whether
      // popup already existed or it's new one
      let rawX: number, rawY: number;

      if (this.userMovedPopup) {
        // 1) Popup existed before: use current left/top as raw coords for reposition
        const currentLeft = parseFloat(popupEl.style.left || '0');
        const currentTop = parseFloat(popupEl.style.top || '0');
        rawX = currentLeft;
        rawY = currentTop;
      } else {
        // 2) Popup freshly created: project its geo‐coordinate
        //    bottom‐center point to screen, then offset by its dimensions:
        const coord = this.popup.getLngLat();
        const screenPt = map.project([coord.lng, coord.lat]);
        rawX = screenPt.x - popupEl.offsetWidth / 2;
        rawY = screenPt.y - popupEl.offsetHeight;
      }

      const { x: finalX, y: finalY } = this.getClampedCoordinates(
        map,
        popupEl,
        rawX,
        rawY
      );
      popupEl.style.left = `${finalX}px`;
      popupEl.style.top = `${finalY}px`;
    });

    ro.observe(popupEl);

    this.popup.on('close', () => {
      ro.unobserve(popupEl);
      this.appRef.detachView(this.popupComponentRef!.hostView);
      this.popupComponentRef!.destroy();
    });

    this.enableDragOnPopup();
  }

  private enableDragOnPopup() {
    const popupEl = this.popup.getElement() as HTMLElement;
    if (!popupEl) return;

    requestAnimationFrame(() => {
      // grab the inner “wrapper” that has the class popup-container
      const containerEl = popupEl.querySelector(
        '.popup-container'
      ) as HTMLElement;
      // grab drag-button
      const handleEl = popupEl.querySelector(
        '.drag-button'
      ) as HTMLElement | null;

      if (!containerEl) return;

      const map = this.mapboxService.map;
      const mapContainer = map.getContainer() as HTMLElement;
      let dragging = false;
      let dragOffsetX = 0;
      let dragOffsetY = 0;

      const isDragOrigin = (target: EventTarget | null) => {
        const el = target as HTMLElement | null;
        return !!el && (el === containerEl || !!el.closest('.drag-button'));
      };

      const onMouseMove = (evt: MouseEvent) => {
        if (!dragging) return;

        // Find mouse position relative to map container:
        const mapRect = mapContainer.getBoundingClientRect();
        const rawX = evt.clientX - mapRect.left - dragOffsetX;
        const rawY = evt.clientY - mapRect.top - dragOffsetY;

        const { x: clampedX, y: clampedY } = this.getClampedCoordinates(
          map,
          popupEl,
          rawX,
          rawY
        );

        this.leftPosition = clampedX;
        this.topPosition = clampedY;

        popupEl.style.left = `${this.leftPosition}px`;
        popupEl.style.top = `${this.topPosition}px`;
      };

      const onMouseUp = (_: MouseEvent) => {
        if (!dragging) return;
        dragging = false;
        popupEl.style.cursor = 'unset';

        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      const onMouseDown = (evt: MouseEvent) => {
        // Prevent popup drag while map moves as it would lead to simultanious changes
        // to transform: translate values from mapbox inner mechanisms and our drag handler
        if (map.isMoving() || !isDragOrigin(evt.target)) return;

        evt.preventDefault();
        this.userMovedPopup = true;
        popupEl.style.cursor = 'move';

        // Grab the popup’s bounding box to find cursor offset
        const popupRect = popupEl.getBoundingClientRect();
        // How far inside the popup the user clicked:
        dragOffsetX = evt.clientX - popupRect.left;
        dragOffsetY = evt.clientY - popupRect.top;
        dragging = true;
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      };

      const clamp = (rawX: number, rawY: number) => {
        const { x, y } = this.getClampedCoordinates(map, popupEl, rawX, rawY);

        this.leftPosition = x;
        this.topPosition = y;

        popupEl.style.left = `${this.leftPosition}px`;
        popupEl.style.top = `${this.topPosition}px`;
      };

      // TOUCH EVENTS (mobile)
      const onTouchMove = (evt: TouchEvent) => {
        if (!dragging || evt.touches.length === 0) return;
        // Prevent page scroll and overscroll (pull-to-refresh) while dragging the popup.
        evt.preventDefault();
        const touch = evt.touches[0];
        const mapRect = mapContainer.getBoundingClientRect();
        const rawX = touch.clientX - mapRect.left - dragOffsetX;
        const rawY = touch.clientY - mapRect.top - dragOffsetY;
        clamp(rawX, rawY);
      };

      const onTouchEnd = (_: TouchEvent) => {
        if (!dragging) return;
        dragging = false;
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('touchend', onTouchEnd);
      };

      const onTouchStart = (evt: TouchEvent) => {
        // Two-finger gesture: block browser page zoom without interfering with map zoom.
        // Must be non-passive (listener is registered with passive:false) to allow preventDefault.
        if (evt.touches.length >= 2) {
          evt.preventDefault();
          return;
        }

        if (
          map.isMoving() ||
          evt.touches.length === 0 ||
          // Only start drag from the designated handle or the popup background —
          // the same rule as onMouseDown. Without this guard any single-finger
          // scroll inside the content list would move the popup instead of scrolling it.
          !isDragOrigin(evt.target)
        )
          return;

        // Prevent Chrome mobile pull-to-refresh and page scroll during popup drag.
        evt.preventDefault();
        this.userMovedPopup = true;

        const touch = evt.touches[0];
        const popupRect = popupEl.getBoundingClientRect();
        dragOffsetX = touch.clientX - popupRect.left;
        dragOffsetY = touch.clientY - popupRect.top;
        dragging = true;

        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', onTouchEnd);
      };

      // Remove any previously attached handler, then attach ours:
      popupEl.ontouchstart = null;
      popupEl.onmousedown = null;

      if (handleEl) {
        handleEl.ontouchstart = null;
        handleEl.onmousedown = null;
      }

      popupEl.addEventListener('mousedown', onMouseDown);
      popupEl.addEventListener('touchstart', onTouchStart, { passive: false });

      if (handleEl) {
        handleEl.addEventListener('mousedown', onMouseDown);
        handleEl.addEventListener('touchstart', onTouchStart as any, {
          passive: false,
        });
      }
    });
  }

  private getClampedCoordinates(
    map: mapboxgl.Map,
    popupEl: HTMLElement,
    rawX: number,
    rawY: number
  ): { x: number; y: number } {
    const isMobile = this.isMobileService.isMobile.value;

    const mapRect = map.getContainer().getBoundingClientRect();

    const maxX = mapRect.width - popupEl.offsetWidth - 1;
    const maxY = isMobile
      ? mapRect.height - popupEl.offsetHeight - 70
      : mapRect.height - popupEl.offsetHeight;
    const minX = 1; // Make at least 1px away from navbar

    let clampedX = rawX;
    let clampedY = rawY;

    if (clampedX < 0) clampedX = 0;
    if (clampedX > maxX) clampedX = maxX;
    if (clampedY < 0) clampedY = 0;
    if (clampedY > maxY) clampedY = maxY;
    if (clampedX < minX) clampedX = minX;

    return { x: clampedX, y: clampedY };
  }

  public updatePopupContent(data: PopupData): void {
    if (this.popupComponentRef) {
      // Concat existing data with updated, reassign of data runs change detection
      this.popupComponentRef.instance.data = {
        type: data.type,
        properties: {
          ...this.popupComponentRef.instance.data.properties,
          ...data.properties,
        },
        feature: data.feature,
      };
      return;
    }
  }

  private async handleGeocoderRequest(
    featureID: Feature['id'],
    coordinates: Position
  ): Promise<{
    id: Feature['id'];
    data: CellGeoData;
  }> {
    this.geocoderService.reverseGeocoder.query(
      `${coordinates[1]},${coordinates[0]}`
    );

    const { id, data } = await this.handleGeocoderResult(featureID);
    return { id, data };
  }

  private handleGeocoderResult(
    id: Feature['id']
  ): Promise<{ id: Feature['id']; data: CellGeoData }> {
    return new Promise((res) => {
      const resultHandler = (event: any) => {
        this.geocoderService.reverseGeocoder.off('result', resultHandler);
        res({ id: id, data: event.result });
      };

      this.geocoderService.reverseGeocoder.on('result', resultHandler);
    });
  }

  private updateTitle(feature: MapboxGeoJSONFeature): void {
    const id = feature.id;
    const levelLayer = feature.layer.id;
    const defaultName = feature!.properties!.name;
    const cellData = this.geocoderService.geocoderCache.getCellData(id);

    if (!cellData) {
      return;
    }

    let title = geocoderContextToString(cellData, levelLayer, defaultName);

    // Fallback to coordinates if no title could be constructed
    if (!title.trim()) {
      title = `lng: ${cellData.center[0].toFixed(
        2
      )} lat: ${cellData.center[1].toFixed(2)}`;
    }

    feature.properties!.addressToCompare = title;
    this.setTitle(title);
  }

  private handlePointsPopup(feature: MapboxGeoJSONFeature): void {
    this.popup.once('open', () => {
      this.assignSelectedStatus(true, feature);
    });

    this.popup.once('close', () => {
      this.assignSelectedStatus(false, feature);
    });
  }

  private assignSelectedStatus(
    status: boolean,
    feature: MapboxGeoJSONFeature
  ): void {
    this.mapboxService.map.setFeatureState(feature, { selected: status });
  }

  private setTitle(title: string): void {
    this.popupTitle = title;

    // Also set title for selected location in reports
    this.reportsService.selectedLocation = title;
  }

  public downloadXlsx(): void {
    if (!this.currentlySelectedId || !this.currentlySelectedLevel) {
      return;
    }

    this.mapHttpService
      .downloadXlsx(
        this.currentlySelectedId,
        convertLayerIdToCellType(this.currentlySelectedLevel)!
      )
      .subscribe((data) => {
        const currentTime = this.datePipe.transform(
          new Date(),
          'yyyy-MM-dd HH:mm:ss'
        );

        const blob: Blob = data as Blob;
        let link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `${this.currentlySelectedLevel}_${
          this.currentlySelectedId
        }_${currentTime}.xlsx`;
        link.click();
      });
  }

  public toggleSeeMore(): void {
    this.isSeeMoreActive = !this.isSeeMoreActive;
  }

  public handleRequestReport(): void {
    // The tile is the location and Community Compass is the report for it, so
    // there is nothing left to ask - open on the summary, where both are still
    // editable.
    this.reportsService.enterOrderFlow({
      at: 'review',
      type: ReportTypes.COMMUNITY_COMPASS,
      geometry: 'cell',
    });

    this.router.navigate(['/reports/order'], { queryParamsHandling: 'merge' });
  }

  public handleClose(): void {
    this.selectedCellService.deselectCell();
    this.userMovedPopup = false;
    this.popup.remove();
  }
}
